import React, { useEffect, useState } from "react";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Quote } from "lucide-react";

const EMPTY = { name: "", role: "", location: "", quote: "", category: "" };

export default function TestimonialsAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = async () => {
    try {
      const r = await adminApi.get("/testimonials");
      setItems(r.data || []);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await adminApi.post("/testimonials", form);
      toast.success("Testimonial added");
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    try {
      await adminApi.delete(`/testimonials/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="space-y-10" data-testid="admin-testimonials-tab">
      <form onSubmit={submit} className="industrial-card p-6 md:p-8 space-y-5">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl uppercase tracking-tight">New testimonial</h3>
          <Plus size={18} className="text-[#e63946]" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="label-mono">Name *</label>
            <input className="vps-input" value={form.name} onChange={(e) => upd("name", e.target.value)} required data-testid="admin-testimonial-name" />
          </div>
          <div>
            <label className="label-mono">Role</label>
            <input className="vps-input" value={form.role} onChange={(e) => upd("role", e.target.value)} placeholder="e.g. Fleet Operator" data-testid="admin-testimonial-role" />
          </div>
          <div>
            <label className="label-mono">Location</label>
            <input className="vps-input" value={form.location} onChange={(e) => upd("location", e.target.value)} placeholder="e.g. Hyderabad" data-testid="admin-testimonial-location" />
          </div>
          <div>
            <label className="label-mono">Category tag</label>
            <input className="vps-input" value={form.category} onChange={(e) => upd("category", e.target.value)} placeholder="e.g. Solar Street Lights" data-testid="admin-testimonial-category" />
          </div>
          <div className="md:col-span-2">
            <label className="label-mono">Quote *</label>
            <textarea className="vps-input" rows={4} value={form.quote} onChange={(e) => upd("quote", e.target.value)} required maxLength={1000} data-testid="admin-testimonial-quote" />
          </div>
        </div>
        <button type="submit" disabled={saving} className="btn-primary" data-testid="admin-testimonial-submit">
          {saving ? "Saving…" : "Add testimonial"}
        </button>
      </form>

      <div>
        <h3 className="font-display text-xl uppercase tracking-tight mb-5">
          Published testimonials ({items.length})
        </h3>
        {items.length === 0 ? (
          <div className="text-zinc-500 text-sm">No testimonials yet.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {items.map((t) => (
              <div key={t.id} className="industrial-card p-5" data-testid={`admin-testimonial-card-${t.id}`}>
                <Quote size={18} className="text-[#e63946]" />
                <p className="text-sm text-zinc-200 mt-3 leading-relaxed line-clamp-4">“{t.quote}”</p>
                <div className="mt-4 pt-3 border-t border-white/10 flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold">{t.name}</div>
                    <div className="font-mono text-[10px] text-zinc-500 uppercase tracking-[0.2em] mt-1">
                      {t.role}{t.location ? ` · ${t.location}` : ""}
                    </div>
                  </div>
                  {t.category && <span className="tag-chip tag-chip-red">{t.category}</span>}
                </div>
                <button onClick={() => handleDelete(t.id)} className="mt-4 text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 hover:text-[#e63946] inline-flex items-center gap-1" data-testid={`admin-testimonial-delete-${t.id}`}>
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
